import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Martini, Send, Clock, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import OrderChip from '../Brand/OrderChip';
import { SkeletonRows } from '../Brand/BrandLoading';
import { ukDate, ukTime } from '../../lib/format';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const lastSent = (value) => {
  if (!value) return 'No reminder sent yet';
  try {
    return `Reminder sent ${new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`;
  } catch (err) {
    return 'Reminder sent';
  }
};

export const CocktailChaseList = ({ token }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/cocktail-chase`, { headers });
      setJobs(res.data.jobs || []);
    } catch (err) {
      toast.error('Could not load the chase list');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const send = async (jobIds) => {
    if (jobIds.length === 0) return;
    setBusy(jobIds.length === 1 ? jobIds[0] : 'bulk');
    try {
      const res = await axios.post(`${API}/admin/cocktail-chase/send-reminders`, { job_ids: jobIds }, { headers });
      toast.success(res.data.message + (res.data.email_sent ? '' : ' (email not connected yet — logged instead)'));
      setSelected([]);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send those reminders');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card className="bg-white" data-testid="cocktail-chase-list">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Martini className="w-5 h-5 text-gold-deep" />
          Cocktail chase list ({jobs.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-slate-600">
          Cocktail jobs in the next 30 days where the customer has not chosen their cocktails yet.
        </p>

        {loading && <SkeletonRows rows={4} testId="chase-loading" />}

        {!loading && jobs.length === 0 && (
          <p className="py-4 text-sm text-slate-500" data-testid="chase-empty">
            Nothing to chase — every upcoming cocktail job has its choices in.
          </p>
        )}

        {!loading && jobs.length > 0 && (
          <>
            <div className="flex flex-wrap items-center gap-3 rounded-[10px] border border-hair bg-page p-3">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <Checkbox
                  checked={selected.length === jobs.filter((j) => j.can_send).length && selected.length > 0}
                  onCheckedChange={(checked) => setSelected(checked ? jobs.filter((j) => j.can_send).map((j) => j.job_id) : [])}
                  data-testid="chase-select-all"
                />
                Select all that can be emailed
              </label>
              <Button
                size="sm" className="ml-auto"
                disabled={selected.length === 0 || busy === 'bulk'}
                onClick={() => send(selected)}
                data-testid="chase-send-bulk"
              >
                {busy === 'bulk' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Send {selected.length > 0 ? `${selected.length} ` : ''}reminders
              </Button>
            </div>

            <div className="space-y-2">
              {jobs.map((job) => (
                <div
                  key={job.job_id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-[10px] border border-hair p-3 transition-colors hover:bg-cream"
                  data-testid={`chase-job-${job.job_id}`}
                >
                  <div className="flex items-start gap-3 min-w-0">
                    {job.can_send && (
                      <Checkbox
                        checked={selected.includes(job.job_id)}
                        onCheckedChange={() => setSelected((prev) =>
                          prev.includes(job.job_id) ? prev.filter((id) => id !== job.job_id) : [...prev, job.job_id])}
                        className="mt-1"
                        data-testid={`chase-select-${job.job_id}`}
                      />
                    )}
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-bold text-ink">{job.client_name}</p>
                        <OrderChip value={job.order_number || job.job_number} fallback="" />
                      </div>
                      <p className="text-xs text-slate-500">
                        {ukDate(job.date)}{job.time ? ` · ${ukTime(job.time) || job.time}` : ''} · {job.job_type}
                      </p>
                      <p className="text-xs text-slate-400">{lastSent(job.reminder_last_sent)}</p>
                      {!job.can_send && (
                        <p className="text-xs text-amber-700 flex items-center gap-1 mt-1">
                          <AlertTriangle className="w-3 h-3" />
                          {!job.client_email ? 'No customer email' : 'No order number'} — add it to send the link
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Badge
                      className={`text-xs ${job.days_until <= 7 ? 'bg-red-100 text-red-800' : job.days_until <= 14 ? 'bg-amber-100 text-amber-800' : 'bg-cream text-slate-700'}`}
                      data-testid={`chase-days-${job.job_id}`}
                    >
                      <Clock className="w-3 h-3 mr-1" />
                      {job.days_until === 0 ? 'Today' : `${job.days_until} day${job.days_until === 1 ? '' : 's'}`}
                    </Badge>
                    <Button
                      size="sm" variant="outline"
                      className="text-gold-deep"
                      disabled={!job.can_send || busy === job.job_id}
                      onClick={() => send([job.job_id])}
                      data-testid={`chase-send-${job.job_id}`}
                    >
                      {busy === job.job_id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                      Send reminder
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default CocktailChaseList;
